import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, ShoppingCart, Trash2, Eye, CreditCard, Check } from 'lucide-react';
import {
  Field, Messages, SearchSelect, Table, capabilitiesAllow, decAdd, decCmp, decMul, decimalProps, displayDec,
  errorCode, errorMessage, formatWhen, isDecimal, isPositiveDecimal, money, scopeKey, shortId, statusLabel,
  useMktRuntime, useMktScope, useStableIds,
} from './MarketplaceShared';
import './Marketplace.css';

const TABS = [['catalogue', 'Catalogue'], ['cart', 'Cart'], ['orders', 'Orders']];
const PRICE_CHANGED = 'Prices changed since you reviewed this order. Review the updated total and confirm again.';

export function buildTimeline(detail) {
  const order = detail?.order;
  if (!order) return [];
  const events = [];
  const push = (at, text) => { if (at) events.push({ at, text }); };
  push(order.created_at, 'Order placed');
  push(order.paid_at, 'Payment received');
  push(order.allocated_at, 'Allocated by supplier');
  (detail.fulfilments || []).forEach(f => {
    push(f.shipped_at, `Shipped: ${f.display_name || 'item'}`);
    push(f.received_at, `Received: ${f.display_name || 'item'}${f.status === 'discrepancy' ? ' (discrepancy)' : ''}`);
  });
  push(order.closed_at, order.status === 'closed_with_discrepancy' ? 'Closed with discrepancy' : 'Delivered');
  push(order.cancelled_at, `Cancelled${order.cancel_reason ? `: ${order.cancel_reason}` : ''}`);
  return events.sort((a, b) => String(a.at).localeCompare(String(b.at)));
}

export default function MarketplaceBuyer() {
  const scope = useMktScope();
  return <BuyerWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function BuyerWorkspace({ scope }) {
  const { active, request, run, busy, error, notice, setError, setNotice } = useMktRuntime(scope);
  const { idFor, release } = useStableIds();
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState('');
  const [tab, setTab] = useState('catalogue');
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [items, setItems] = useState([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [addQty, setAddQty] = useState({});
  const [carts, setCarts] = useState({});
  const [cartWarning, setCartWarning] = useState('');
  const [quote, setQuote] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [payLink, setPayLink] = useState(null);
  const [receipts, setReceipts] = useState({});
  const dirty = useRef(false);
  const supplierId = supplier?.value || '';
  const cartLines = useMemo(() => carts[supplierId] || [], [carts, supplierId]);
  const canOrder = Boolean(caps?.can_operate) && Boolean(scope.locationId);

  const loadBase = useCallback(async () => {
    setLoading(true); setBlocked(''); setError('');
    try {
      if (!scope.orgId || !scope.token) throw new Error('Sign in and select an organization to use the marketplace.');
      const c = await request('get', '/capabilities');
      if (!active()) return;
      if (!capabilitiesAllow(c)) { setCaps(null); setBlocked('Marketplace is not enabled for this organization, or you do not have access.'); return; }
      setCaps(c);
      const rels = await request('get', '/relationships', { params: { side: 'customer' } });
      if (!active()) return;
      let names = {};
      if (c.can_write) {
        try {
          const orgs = await request('get', '/lookups/organizations');
          names = Object.fromEntries((Array.isArray(orgs) ? orgs : []).map(o => [o.id, o.name]));
        } catch { names = {}; }
      }
      if (!active()) return;
      const options = (Array.isArray(rels) ? rels : [])
        .filter(r => r.status === 'active')
        .map(r => ({ value: r.supplier_org_id, label: names[r.supplier_org_id] || `Supplier ${r.supplier_org_id}`, settlement: r.settlement_terms }));
      setSuppliers(options);
      if (options.length === 1) setSupplier(options[0]);
    } catch (e) { if (active()) { setCaps(null); setBlocked(errorMessage(e)); } }
    finally { if (active()) setLoading(false); }
  }, [scope, request, active, setError]);
  useEffect(() => { loadBase(); }, [loadBase]);

  useEffect(() => {
    if (!caps || !supplierId) { setItems([]); return undefined; }
    let cancelled = false;
    (async () => {
      setCatalogueLoading(true);
      try {
        const data = await request('get', '/buyer/catalogue', { params: { supplier_org_id: supplierId } });
        if (!active() || cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) { if (active() && !cancelled) { setItems([]); setError(errorMessage(e)); } }
      finally { if (active() && !cancelled) setCatalogueLoading(false); }
      if (cancelled || !active()) return;
      try {
        const server = await request('get', '/cart', { params: { supplier_org_id: supplierId } });
        if (!active() || cancelled) return;
        const lines = (server?.lines || []).filter(l => l?.item_id && isPositiveDecimal(String(l.qty)))
          .map(l => ({ item_id: l.item_id, qty: String(l.qty) }));
        setCarts(prev => (prev[supplierId] === undefined ? { ...prev, [supplierId]: lines } : prev));
      } catch { setCarts(prev => (prev[supplierId] === undefined ? { ...prev, [supplierId]: [] } : prev)); }
    })();
    return () => { cancelled = true; };
  }, [caps, supplierId, request, active, setError]);

  useEffect(() => {
    if (!dirty.current || !supplierId || !canOrder) return undefined;
    const timer = setTimeout(async () => {
      try {
        await request('put', '/cart', { body: { supplier_org_id: supplierId, lines: cartLines.map(l => ({ item_id: l.item_id, qty: l.qty.trim() })) } });
        if (active()) setCartWarning('');
      } catch { if (active()) setCartWarning('Cart could not be saved to the server. You can still check out from this device.'); }
    }, 600);
    return () => clearTimeout(timer);
  }, [cartLines, supplierId, canOrder, request, active]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const data = await request('get', '/orders', { params: { side: 'customer' } });
      if (active()) setOrders(Array.isArray(data) ? data : []);
    } catch (e) { if (active()) { setOrders([]); setError(errorMessage(e)); } }
    finally { if (active()) setOrdersLoading(false); }
  }, [request, active, setError]);
  useEffect(() => { if (caps && tab === 'orders') loadOrders(); }, [caps, tab, loadOrders]);

  const openOrder = useCallback(async orderId => {
    setPayLink(null);
    const r = await run(() => request('get', `/orders/${encodeURIComponent(orderId)}`));
    if (r?.ok && active()) {
      setDetail(r.result);
      setReceipts({});
    }
  }, [run, request, active]);

  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.item_id, i])), [items]);
  const categories = useMemo(() => [...new Set(items.map(i => i.category).filter(Boolean))].sort(), [items]);
  const visible = items.filter(i => (!category || i.category === category)
    && (!search.trim() || (i.display_name || '').toLowerCase().includes(search.trim().toLowerCase())));

  const updateCart = updater => {
    dirty.current = true;
    setQuote(null);
    setCarts(prev => ({ ...prev, [supplierId]: updater(prev[supplierId] || []) }));
  };
  const addToCart = item => {
    const qty = (addQty[item.item_id] ?? '1').trim();
    if (!isPositiveDecimal(qty)) { setError(`Enter a quantity greater than zero for ${item.display_name}.`); return; }
    setError(''); setNotice('');
    updateCart(lines => (lines.some(l => l.item_id === item.item_id)
      ? lines.map(l => (l.item_id === item.item_id ? { ...l, qty: decAdd(l.qty, qty) } : l))
      : [...lines, { item_id: item.item_id, qty }]));
    setNotice(`${item.display_name} added to cart.`);
  };

  const checkoutLines = () => cartLines.map(l => ({ item_id: l.item_id, qty: l.qty.trim() }));
  const validateCart = () => {
    if (!supplierId) return 'Choose a supplier first.';
    if (!scope.locationId) return 'Select a site before checking out.';
    if (!cartLines.length) return 'Your cart is empty.';
    const bad = cartLines.find(l => !isPositiveDecimal(l.qty));
    if (bad) return `Quantity for ${itemById[bad.item_id]?.display_name || bad.item_id} must be greater than zero.`;
    const gone = cartLines.find(l => !itemById[l.item_id]);
    if (gone) return 'Some cart items are no longer available. Remove them to continue.';
    return '';
  };
  const checkoutBody = (lines, token) => {
    const fingerprint = ['checkout', supplierId, scope.locationId, lines, token || ''];
    return {
      fingerprint,
      body: {
        command_id: idFor(fingerprint), supplier_org_id: supplierId, buyer_site_id: scope.locationId, lines,
        ...(token ? { confirm_price_token: token } : {}),
      },
    };
  };
  const toQuote = (d, lines) => ({
    token: d.confirm_price_token, grand_total: d.grand_total, currency: d.currency, settlement: d.settlement,
    lines: d.lines || [], signature: JSON.stringify(lines),
  });
  const finishOrder = d => {
    dirty.current = false;
    setCarts(prev => ({ ...prev, [supplierId]: [] }));
    setQuote(null);
    setDetail({ order: d.order, allocations: [], fulfilments: [] });
    setTab('orders');
    setNotice(d.created === false ? 'This order was already placed.' : 'Order placed.');
    loadOrders();
  };

  const review = async () => {
    const problem = validateCart();
    if (problem) { setError(problem); return; }
    setNotice('');
    const lines = checkoutLines();
    const { body } = checkoutBody(lines);
    const r = await run(() => request('post', '/checkout', { body }));
    if (!r?.ok || !active()) return;
    if (r.result.requires_confirmation) setQuote(toQuote(r.result, lines));
    else if (r.result.order) finishOrder(r.result);
  };

  const confirm = async () => {
    if (!quote) return;
    const problem = validateCart();
    if (problem) { setError(problem); return; }
    const lines = checkoutLines();
    if (JSON.stringify(lines) !== quote.signature) { setQuote(null); setError('The cart changed after review. Review the order again.'); return; }
    const { body, fingerprint } = checkoutBody(lines, quote.token);
    const r = await run(() => request('post', '/checkout', { body }));
    if (!r || !active()) return;
    if (r.ok) { release(fingerprint); finishOrder(r.result); return; }
    if (errorCode(r.error) === 'price_changed') {
      const fresh = checkoutBody(lines);
      const again = await run(() => request('post', '/checkout', { body: fresh.body }));
      if (again?.ok && active() && again.result.requires_confirmation) {
        setQuote(toQuote(again.result, lines));
        setError(PRICE_CHANGED);
      }
    }
  };

  const pay = async order => {
    const fingerprint = ['pay', order.order_id];
    const r = await run(() => request('post', `/orders/${encodeURIComponent(order.order_id)}/payment-session`, { body: { command_id: idFor(fingerprint) } }));
    if (!r?.ok || !active()) return;
    release(fingerprint);
    const url = r.result?.payment_link;
    if (typeof url === 'string' && /^https:\/\//i.test(url)) setPayLink({ orderId: order.order_id, url });
    else setError('Payment session opened but no secure payment link was returned. Try again shortly.');
  };

  const receive = async (alloc, expected) => {
    const form = receipts[alloc.allocation_id] || {};
    const qty = (form.qty ?? expected).trim();
    const reason = (form.reason || '').trim();
    if (!isDecimal(qty)) { setError('Received quantity must be a number, for example 12.5.'); return; }
    if (decCmp(qty, expected) > 0) { setError('Received quantity cannot exceed the shipped quantity.'); return; }
    if (decCmp(qty, expected) < 0 && !reason) { setError('Give a loss reason for a short receipt.'); return; }
    const short = decCmp(qty, expected) < 0;
    const orderId = detail.order.order_id;
    const fingerprint = ['receive', orderId, alloc.allocation_id, qty, short ? reason : ''];
    const body = { command_id: idFor(fingerprint), allocation_id: alloc.allocation_id, received_qty: qty, ...(short ? { loss_reason: reason } : {}) };
    const r = await run(() => request('post', `/orders/${encodeURIComponent(orderId)}/receive`, { body }), 'Receipt recorded.');
    if (r?.ok && active()) { release(fingerprint); await openOrderQuiet(orderId); }
  };
  const openOrderQuiet = async orderId => {
    try {
      const d = await request('get', `/orders/${encodeURIComponent(orderId)}`);
      if (active()) { setDetail(d); loadOrders(); }
    } catch (e) { if (active()) setError(errorMessage(e)); }
  };

  const estimate = cartLines.reduce((sum, l) => {
    const item = itemById[l.item_id];
    return item && isPositiveDecimal(l.qty) ? decAdd(sum, decMul(item.unit_sell_price, l.qty)) : sum;
  }, '0');
  const currency = items[0]?.currency || '';

  return (
    <main className="mkt-ops" data-testid="marketplace-buyer">
      <header>
        <h1>Marketplace</h1>
        <button type="button" title="Refresh" aria-label="Refresh marketplace" disabled={busy || loading} onClick={() => { loadBase(); if (tab === 'orders') loadOrders(); }}><RefreshCw size={18} /></button>
      </header>
      <Messages error={error} notice={notice} />
      {loading && <p role="status">Checking marketplace access...</p>}
      {!loading && blocked && <div className="mkt-error" role="alert">{blocked}</div>}
      {!loading && caps && <>
        {!scope.locationId && <p className="mkt-hint" role="status">Select a site to add items and place orders.</p>}
        <div className="mkt-supplier">
          <SearchSelect label="Supplier" value={supplier} options={suppliers} disabled={busy}
            onChange={s => { setSupplier(s); setQuote(null); setSearch(''); setCategory(''); setNotice(''); }}
            emptyText="No active supplier relationships" />
          {!suppliers.length && <p className="mkt-hint">You have no active supplier relationships yet. A supplier must invite you and you must accept.</p>}
        </div>
        <div className="mkt-tabs" role="tablist" aria-label="Marketplace buyer workflows">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} aria-label={key === 'cart' ? `Cart, ${cartLines.length} lines` : label}
              disabled={busy} onClick={() => { setTab(key); setNotice(''); }}>
              {label}{key === 'cart' && cartLines.length > 0 ? ` (${cartLines.length})` : ''}
            </button>
          ))}
        </div>

        {tab === 'catalogue' && (
          <section role="tabpanel" aria-label="Catalogue">
            {!supplierId ? <p className="mkt-empty">Choose a supplier to browse its catalogue.</p> : <>
              <div className="mkt-filters">
                <Field label="Search catalogue" type="search" value={search} onChange={e => setSearch(e.target.value)} />
                <Field label="Category" value={category} onChange={e => setCategory(e.target.value)} options={[{ value: '', label: 'All categories' }, ...categories.map(c => ({ value: c, label: c }))]} />
              </div>
              {catalogueLoading ? <p role="status">Loading catalogue...</p> : (
                <Table headers={['Item', 'Category', 'Pack', 'Price', 'Quantity', 'Add']} label="Catalogue" empty={!visible.length} emptyText="No catalogue items match.">
                  {visible.map(item => (
                    <tr key={item.item_id}>
                      <td>{item.display_name}</td>
                      <td>{item.category}</td>
                      <td>{displayDec(item.pack_size, 0)} {item.pack_unit}</td>
                      <td className="mkt-number">{money(item.unit_sell_price, item.currency)}</td>
                      <td>
                        <input className="mkt-qty" aria-label={`Quantity of ${item.display_name}`} {...decimalProps} value={addQty[item.item_id] ?? '1'}
                          disabled={!canOrder || busy} onChange={e => setAddQty({ ...addQty, [item.item_id]: e.target.value })} />
                      </td>
                      <td><button type="button" aria-label={`Add ${item.display_name} to cart`} disabled={!canOrder || busy} onClick={() => addToCart(item)}><ShoppingCart size={16} />Add</button></td>
                    </tr>
                  ))}
                </Table>
              )}
            </>}
          </section>
        )}

        {tab === 'cart' && (
          <section role="tabpanel" aria-label="Cart">
            {cartWarning && <p className="mkt-hint" role="status">{cartWarning}</p>}
            {!cartLines.length ? <p className="mkt-empty">Your cart is empty.</p> : <>
              <Table headers={['Item', 'Price', 'Quantity', 'Estimate', 'Remove']} label="Cart">
                {cartLines.map(line => {
                  const item = itemById[line.item_id];
                  const name = item?.display_name || 'Unavailable item';
                  return (
                    <tr key={line.item_id}>
                      <td>{name}{!item && <span className="mkt-flag"> no longer available</span>}</td>
                      <td className="mkt-number">{item ? money(item.unit_sell_price, item.currency) : ''}</td>
                      <td>
                        <input className="mkt-qty" aria-label={`Cart quantity of ${name}`} {...decimalProps} value={line.qty} disabled={busy}
                          onChange={e => updateCart(ls => ls.map(l => (l.item_id === line.item_id ? { ...l, qty: e.target.value } : l)))} />
                      </td>
                      <td className="mkt-number">{item && isPositiveDecimal(line.qty) ? money(decMul(item.unit_sell_price, line.qty), item.currency) : ''}</td>
                      <td><button type="button" aria-label={`Remove ${name} from cart`} disabled={busy} onClick={() => updateCart(ls => ls.filter(l => l.item_id !== line.item_id))}><Trash2 size={16} /></button></td>
                    </tr>
                  );
                })}
              </Table>
              <p className="mkt-total">Estimated total: <strong>{money(estimate, currency)}</strong> <span className="mkt-hint">Final prices are confirmed at review.</span></p>
              {!quote && <button type="button" className="mkt-primary" aria-label="Review order" disabled={busy || !canOrder} onClick={review}>Review order</button>}
            </>}
            {quote && (
              <section className="mkt-detail" aria-label="Order confirmation">
                <h2>Confirm order</h2>
                <Table headers={['Item', 'Quantity', 'Unit price', 'Line total']} label="Confirmation">
                  {quote.lines.map(l => (
                    <tr key={l.item_id}>
                      <td>{l.display_name}</td>
                      <td className="mkt-number">{displayDec(l.qty, 0)} {l.pack_unit}</td>
                      <td className="mkt-number">{money(l.unit_sell_price, quote.currency)}</td>
                      <td className="mkt-number">{money(l.line_total, quote.currency)}</td>
                    </tr>
                  ))}
                </Table>
                <p className="mkt-total">Order total: <strong>{money(quote.grand_total, quote.currency)}</strong> ({statusLabel(quote.settlement)})</p>
                <div className="mkt-actions">
                  <button type="button" className="mkt-primary" aria-label="Confirm and place order" disabled={busy} onClick={confirm}><Check size={16} />Confirm and place order</button>
                  <button type="button" aria-label="Cancel review" disabled={busy} onClick={() => setQuote(null)}>Back to cart</button>
                </div>
              </section>
            )}
          </section>
        )}

        {tab === 'orders' && (
          <section role="tabpanel" aria-label="Orders">
            {ordersLoading ? <p role="status">Loading orders...</p> : (
              <Table headers={['Order', 'Placed', 'Status', 'Settlement', 'Total', 'View']} label="Orders" empty={!orders.length} emptyText="No orders yet.">
                {orders.map(o => (
                  <tr key={o.order_id}>
                    <td>{shortId(o.order_id)}</td>
                    <td>{formatWhen(o.created_at)}</td>
                    <td>{statusLabel(o.status)}</td>
                    <td>{statusLabel(o.settlement)}</td>
                    <td className="mkt-number">{money(o.grand_total, o.currency)}</td>
                    <td><button type="button" aria-label={`View order ${shortId(o.order_id)}`} disabled={busy} onClick={() => openOrder(o.order_id)}><Eye size={16} /></button></td>
                  </tr>
                ))}
              </Table>
            )}
            {detail?.order && <OrderDetail detail={detail} caps={caps} busy={busy} payLink={payLink} pay={pay} receipts={receipts} setReceipts={setReceipts} receive={receive} />}
          </section>
        )}
      </>}
    </main>
  );
}

function OrderDetail({ detail, caps, busy, payLink, pay, receipts, setReceipts, receive }) {
  const { order, allocations = [], fulfilments = [] } = detail;
  const timeline = buildTimeline(detail);
  const lineName = id => order.lines?.find(l => l.item_id === id)?.display_name || 'Item';
  const awaiting = allocations.filter(a => ['shipped', 'po_drafted'].includes(a.status));
  return (
    <section className="mkt-detail" aria-label={`Order ${shortId(order.order_id)} detail`}>
      <h2>Order {shortId(order.order_id)}</h2>
      <p>{statusLabel(order.status)} | {statusLabel(order.settlement)} | Total {money(order.grand_total, order.currency)}</p>
      <Table headers={['Item', 'Quantity', 'Unit price', 'Line total']} label="Order lines">
        {(order.lines || []).map(l => (
          <tr key={l.item_id}>
            <td>{l.display_name}</td>
            <td className="mkt-number">{displayDec(l.qty, 0)} x {displayDec(l.pack_size, 0)} {l.pack_unit}</td>
            <td className="mkt-number">{money(l.unit_sell_price, order.currency)}</td>
            <td className="mkt-number">{money(l.line_total, order.currency)}</td>
          </tr>
        ))}
      </Table>
      <p>Subtotal {money(order.subtotal, order.currency)} | Tax {money(order.tax_total, order.currency)} | Total {money(order.grand_total, order.currency)}</p>
      {order.status === 'awaiting_payment' && order.settlement === 'prepaid_card' && caps?.can_operate && (
        <div className="mkt-actions">
          <button type="button" className="mkt-primary" aria-label="Pay by card" disabled={busy} onClick={() => pay(order)}><CreditCard size={16} />Pay by card</button>
          {payLink?.orderId === order.order_id && <a href={payLink.url} target="_blank" rel="noopener noreferrer">Open secure payment page</a>}
        </div>
      )}
      {order.status === 'intercompany_pending' && <p className="mkt-hint">Awaiting intercompany approval by the supplier.</p>}
      <h3>Timeline</h3>
      <ol className="mkt-timeline" aria-label="Order timeline">
        {timeline.map((e, i) => <li key={`${e.at}-${i}`}><time dateTime={e.at}>{formatWhen(e.at)}</time> {e.text}</li>)}
      </ol>
      {fulfilments.length > 0 && (
        <Table headers={['Shipment', 'Shipped', 'Received', 'Status']} label="Shipments">
          {fulfilments.map(f => (
            <tr key={f.fulfilment_id}>
              <td>{f.display_name || lineName(f.item_id)}</td>
              <td className="mkt-number">{displayDec(f.qty_shipped, 0)} {f.uom}</td>
              <td className="mkt-number">{f.qty_received == null ? 'Pending' : `${displayDec(f.qty_received, 0)} ${f.uom || ''}`}</td>
              <td>{statusLabel(f.status)}{f.loss_reason ? `: ${f.loss_reason}` : ''}</td>
            </tr>
          ))}
        </Table>
      )}
      {caps?.can_operate && awaiting.map(alloc => {
        const shipment = fulfilments.find(f => f.allocation_id === alloc.allocation_id);
        const expected = String(shipment?.qty_shipped ?? alloc.shipped_qty ?? alloc.base_qty ?? '');
        if (!isDecimal(expected)) return null;
        const form = receipts[alloc.allocation_id] || {};
        const qty = form.qty ?? expected;
        const short = isDecimal(qty) && decCmp(qty, expected) < 0;
        const name = lineName(alloc.line_item_id);
        return (
          <form key={alloc.allocation_id} className="mkt-form" aria-label={`Receive ${name}`} onSubmit={e => { e.preventDefault(); receive(alloc, expected); }}>
            <h3>Receive {name}</h3>
            <fieldset disabled={busy}>
              <div className="mkt-fields">
                <Field label={`Received quantity for ${name}`} {...decimalProps} value={qty} onChange={e => setReceipts({ ...receipts, [alloc.allocation_id]: { ...form, qty: e.target.value } })} />
                {short && <Field label={`Loss reason for ${name}`} type="text" value={form.reason || ''} onChange={e => setReceipts({ ...receipts, [alloc.allocation_id]: { ...form, reason: e.target.value } })} />}
              </div>
              <p className="mkt-hint">Expected {displayDec(expected, 0)} {alloc.uom || ''}</p>
              <button type="submit" className="mkt-primary">Confirm receipt</button>
            </fieldset>
          </form>
        );
      })}
    </section>
  );
}
